import { useEffect } from 'react';
import { gql, useLazyQuery } from '@apollo/client';

import { MockedResponse } from '@nerdwallet/apollo/testing';

import { useIsNerdWalletPlusMember } from '@lib/hooks/nerdwallet-plus';
import { CURRENT_CANOPY_TOS_VERSION } from '@lib/hooks/insurance/use-canopy-terms-query';
import { getLogger } from '@lib/logging';

import type { PolicyToReview } from './canopy-flow-state';

const logger = getLogger('UseStartCanopyFlow');

const INITIAL_CANOPY_FLOW_DATA = gql`
  query GetCanopyFlowInitialData {
    canopyTermsOfServiceAgreements {
      success
      acknowledgements {
        source
        type
        version
      }
    }
    hasAcknowledgedCoveredTermsOfService {
      acknowledged
    }
    insurancePolicies {
      policies {
        id
        carrierName
        policyType
      }
    }
  }
`;

type UseStartCanopyFlow = {
  isEnabled: boolean;
  onDone: () => void;
  onReady: (
    hasGivenConsent: boolean,
    isNerdWalletPlusMember: boolean,
    policyToReview: PolicyToReview,
  ) => void;
};

const useStartCanopyFlow = ({
  onDone,
  onReady,
  isEnabled,
}: UseStartCanopyFlow) => {
  const { isNerdWalletPlusMember, loading: nwPlusLoading } =
    useIsNerdWalletPlusMember();

  const [initialFlowData, { loading: loadingInitialFlowData }] = useLazyQuery(
    INITIAL_CANOPY_FLOW_DATA,
  );

  useEffect(() => {
    async function startFlow() {
      if (!isEnabled) {
        return null;
      }

      if (loadingInitialFlowData) {
        logger.info('Fetching initial canopy flow data...');
      }

      if (nwPlusLoading) {
        logger.info('Waiting for data...');
        return;
      }

      const { data, error } = await initialFlowData();
      if (error) {
        logger.error('Error fetching initial canopy flow data', error);
        onDone();
        return;
      }

      const hasAcceptedCanopyTerms =
        data?.canopyTermsOfServiceAgreements?.acknowledgements?.some(
          (ack) => ack.version === CURRENT_CANOPY_TOS_VERSION,
        );
      const hasAcknowledgedCoveredTos =
        data.hasAcknowledgedCoveredTermsOfService?.acknowledged;

      const hasGivenConsent =
        hasAcceptedCanopyTerms && hasAcknowledgedCoveredTos;

      const policies = data.insurancePolicies?.policies || [];

      onReady(hasGivenConsent, isNerdWalletPlusMember, policies[0]);
      onDone();
    }

    startFlow();
  }, [
    initialFlowData,
    isEnabled,
    isNerdWalletPlusMember,
    loadingInitialFlowData,
    nwPlusLoading,
    onDone,
    onReady,
  ]);
};

export default useStartCanopyFlow;

export const mockInitialFlowDataResponse = ({
  hasAcceptedCanopyTos = true,
  hasAcceptedCoveredTos = true,
  hasPolicies = true,
}: {
  hasAcceptedCanopyTos?: boolean;
  hasAcceptedCoveredTos?: boolean;
  hasPolicies?: boolean;
} = {}): MockedResponse => ({
  request: {
    query: INITIAL_CANOPY_FLOW_DATA,
  },
  result: {
    data: {
      canopyTermsOfServiceAgreements: {
        success: hasAcceptedCanopyTos,
        acknowledgements: hasAcceptedCanopyTos
          ? [
              {
                source: 'my-nerdwallet-next',
                type: 'CANOPY',
                version: '1',
              },
            ]
          : null,
      },
      hasAcknowledgedCoveredTermsOfService: {
        acknowledged: hasAcceptedCoveredTos,
      },
      insurancePolicies: {
        policies: hasPolicies
          ? [
              {
                id: 1,
                carrierName: 'StateFarm',
                policyType: 'AUTO',
              },
            ]
          : [],
      },
    },
  },
});
